import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import { toast } from "sonner";
import { Trash2, X, FolderKanban, Pencil } from "lucide-react";

const STATUS_STYLE = {
  planning: "bg-gray-100 text-gray-800 border-gray-300",
  active: "bg-emerald-50 text-emerald-800 border-emerald-200",
  on_hold: "bg-amber-50 text-amber-800 border-amber-200",
  closed: "bg-gray-100 text-gray-600 border-gray-300",
};

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [sites, setSites] = useState([]);
  const [editing, setEditing] = useState(null);

  const refresh = useCallback(async () => {
    const [p, s] = await Promise.all([api.get("/projects"), api.get("/sites")]);
    setProjects(p.data); setSites(s.data);
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const remove = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    await api.delete(`/projects/${id}`);
    refresh();
  };

  return (
    <div>
      <PageHeader
        title="Projects"
        subtitle="Capital improvement portfolio"
        actions={
          <button onClick={() => setEditing("new")} data-testid="new-project-button" className="bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold uppercase tracking-widest px-4 py-2.5">
            + New Project
          </button>
        }
      />
      <div className="p-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="projects-list">
          {projects.length === 0 ? (
            <div className="col-span-full text-center text-sm text-gray-500 py-12 bg-white border border-gray-200">
              <FolderKanban className="w-8 h-8 text-gray-400 mx-auto mb-3" />
              No projects yet. Create your first capital project.
            </div>
          ) : projects.map((p) => {
            const pct = p.material_budget_tons > 0 ? Math.min(100, (p.consumed_tons / p.material_budget_tons) * 100) : 0;
            return (
              <div key={p.id} className="bg-white border border-gray-200 p-5" data-testid={`project-card-${p.code}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="font-mono text-xs text-gray-500 mb-1">{p.code}</div>
                    <div className="font-display font-bold text-gray-900 text-lg leading-tight">{p.name}</div>
                  </div>
                  <span className={`text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 border ${STATUS_STYLE[p.status]}`}>{p.status.replace("_", " ")}</span>
                </div>
                {p.description && <div className="text-sm text-gray-600 mb-3">{p.description}</div>}
                <div className="border-t border-gray-100 pt-3 space-y-1">
                  <div className="flex justify-between text-xs font-mono">
                    <span className="text-gray-500">Excavation Budget</span>
                    <span className="text-gray-900">{(p.material_budget_tons || 0).toLocaleString()} t</span>
                  </div>
                  <div className="flex justify-between text-xs font-mono">
                    <span className="text-gray-500">Import Budget</span>
                    <span className="text-gray-900">{(p.import_budget_tons || 0).toLocaleString()} t</span>
                  </div>
                  {p.approved_disposal_site_ids?.length > 0 && (
                    <div className="text-xs font-mono text-gray-500">Whitelisted drops: {p.approved_disposal_site_ids.length}</div>
                  )}
                  <div className="mt-3">
                    <div className="w-full h-1.5 bg-gray-100 relative">
                      <div className="absolute inset-y-0 left-0 bg-orange-500" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="flex justify-between mt-1 text-[10px] uppercase tracking-widest font-bold text-gray-500">
                      <span>Consumed</span>
                      <span>{(p.consumed_tons || 0).toFixed(1)} t · {pct.toFixed(0)}%</span>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end gap-1 mt-3 pt-3 border-t border-gray-100">
                  <button onClick={() => setEditing(p)} className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100" data-testid={`edit-project-${p.code}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => remove(p.id)} className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50" data-testid={`delete-project-${p.code}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {editing && (
        <ProjectDialog
          initial={editing === "new" ? null : editing}
          sites={sites}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}
    </div>
  );
}

function ProjectDialog({ initial, sites, onClose, onSaved }) {
  const [form, setForm] = useState({
    code: initial?.code || "",
    name: initial?.name || "",
    description: initial?.description || "",
    status: initial?.status || "planning",
    material_budget_tons: initial?.material_budget_tons ?? 0,
    import_budget_tons: initial?.import_budget_tons ?? 0,
    approved_disposal_site_ids: initial?.approved_disposal_site_ids || [],
  });
  const [busy, setBusy] = useState(false);
  const isEdit = !!initial;
  const dropoffSites = sites.filter((s) => s.site_type !== "construction");

  const toggleSite = (siteId) => {
    setForm((f) => ({
      ...f,
      approved_disposal_site_ids: f.approved_disposal_site_ids.includes(siteId)
        ? f.approved_disposal_site_ids.filter((x) => x !== siteId)
        : [...f.approved_disposal_site_ids, siteId],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (isEdit) await api.patch(`/projects/${initial.id}`, form);
      else await api.post("/projects", form);
      toast.success(isEdit ? "Project updated" : "Project created");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white border border-gray-300 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="border-b border-gray-200 px-5 py-4 flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="font-display font-bold text-xl">{isEdit ? "Edit Project" : "New Project"}</div>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <F label="Project Code (unique)">
            <input required value={form.code} onChange={(e) => set("code", e.target.value)} className="inp font-mono" data-testid="project-code-input" placeholder="MDC-2026-014" />
          </F>
          <F label="Name">
            <input required value={form.name} onChange={(e) => set("name", e.target.value)} className="inp" data-testid="project-name-input" />
          </F>
          <F label="Description">
            <textarea rows="2" value={form.description} onChange={(e) => set("description", e.target.value)} className="inp" data-testid="project-desc-input" />
          </F>
          <F label="Status">
            <select value={form.status} onChange={(e) => set("status", e.target.value)} className="inp" data-testid="project-status-select">
              <option value="planning">Planning</option>
              <option value="active">Active</option>
              <option value="on_hold">On Hold</option>
              <option value="closed">Closed</option>
            </select>
          </F>
          <div className="grid grid-cols-2 gap-3">
            <F label="Excavation Budget (t)">
              <input type="number" step="1" value={form.material_budget_tons} onChange={(e) => set("material_budget_tons", parseFloat(e.target.value) || 0)} className="inp" data-testid="project-material-budget-input" />
            </F>
            <F label="Import Budget (t)">
              <input type="number" step="1" value={form.import_budget_tons} onChange={(e) => set("import_budget_tons", parseFloat(e.target.value) || 0)} className="inp" data-testid="project-import-budget-input" />
            </F>
          </div>
          <div className="border-t border-gray-200 pt-4">
            <div className="label-caps mb-2">Approved Disposal Sites</div>
            <div className="text-xs text-gray-500 mb-2">If none selected, all disposal sites are allowed.</div>
            <div className="max-h-40 overflow-y-auto border border-gray-200 divide-y divide-gray-100">
              {dropoffSites.length === 0 && <div className="p-3 text-sm text-gray-500">No dump/fill sites available.</div>}
              {dropoffSites.map((s) => (
                <label key={s.id} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm">
                  <input type="checkbox" checked={form.approved_disposal_site_ids.includes(s.id)} onChange={() => toggleSite(s.id)} className="w-4 h-4 accent-orange-500" data-testid={`project-approved-site-${s.name}`} />
                  <span className="flex-1 truncate">{s.name}</span>
                  <span className="text-[10px] uppercase tracking-widest font-bold text-gray-500">{s.site_type.replace("_", " ")}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex gap-3 pt-3">
            <button type="button" onClick={onClose} className="flex-1 border border-gray-300 py-2.5 text-xs uppercase tracking-widest font-semibold">Cancel</button>
            <button type="submit" disabled={busy} data-testid="submit-project-button" className="flex-1 bg-orange-500 hover:bg-orange-600 text-white py-2.5 text-xs uppercase tracking-widest font-semibold">{isEdit ? "Update" : "Save"}</button>
          </div>
        </form>
        <style>{`.inp { width: 100%; padding: 0.5rem 0.75rem; border: 1px solid #D1D5DB; font-size: 0.875rem; } .inp:focus { outline: none; border-color: #111827; }`}</style>
      </div>
    </div>
  );
}

function F({ label, children }) {
  return <div><label className="label-caps block mb-1.5">{label}</label>{children}</div>;
}
